import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";
import type { InvitationResponse } from "./invitations";

/**
 * Invitee-side surface for cross-client invitations (backend.md §24.7 —
 * mirrors /invitations/{id}/accept|reject but scoped to "addressed to
 * me"). Used by the /settings/invitations page.
 */

export interface CrossClientGrantResponse {
  id: string;
  accessor_user_id: string;
  resource_owner_client_id: string;
  invitation_id: string | null;
  site_ids: string[];
  active: boolean;
  suspended_at: string | null;
  access_start_date: string;
  access_end_date: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  revoke_reason: string | null;
  created_at: string;
  updated_at: string;
}

export function useMyCrossClientInvitations() {
  return useQuery<InvitationResponse[]>({
    queryKey: ["my-invitations"],
    queryFn: async () =>
      (await apiClient.get<InvitationResponse[]>("/me/cross-client-invitations")).data,
    staleTime: 30_000,
  });
}

export function useAcceptMyInvitation() {
  const qc = useQueryClient();
  return useMutation<CrossClientGrantResponse, Error, string>({
    mutationFn: async (invitationId) =>
      (
        await apiClient.post<CrossClientGrantResponse>(
          `/me/cross-client-invitations/${invitationId}/accept`,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-invitations"] });
      // Accepted grant changes PermissionScope on next /me — refetch.
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useRejectMyInvitation() {
  const qc = useQueryClient();
  return useMutation<InvitationResponse, Error, { invitationId: string; reason?: string }>({
    mutationFn: async ({ invitationId, reason }) =>
      (
        await apiClient.post<InvitationResponse>(
          `/me/cross-client-invitations/${invitationId}/reject`,
          { reason: reason ?? null },
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-invitations"] }),
  });
}
